import React, { useEffect, useRef, useState } from 'react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import Cover from '../components/paper/Cover';
import Blocks from '../components/paper/Blocks';
import Rich, { plain } from '../components/paper/Rich';
import { ArrowDown, ArrowRight } from '../components/Icons';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { PAPER, REFERENCES, REFERENCES_NOTE, SECTIONS, refId, sectionId } from '../content/whitepaper';

const CONTENTS = [
  ...SECTIONS.map(({ n, title }) => ({ n, title, id: sectionId(n) })),
  { n: '15', title: 'References', id: sectionId(15) },
];

const HEADER = 68;

// Tracks which section is being read, and fills the progress bar without re-rendering.
function useReading(progressRef) {
  const [active, setActive] = useState(CONTENTS[0].id);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = HEADER + window.innerHeight * 0.25;
      let current = CONTENTS[0].id;
      CONTENTS.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      setActive(current);

      const paper = document.getElementById('contents');
      if (paper && progressRef.current) {
        const rect = paper.getBoundingClientRect();
        const travel = rect.height - window.innerHeight * 0.6;
        const progress = Math.min(1, Math.max(0, (HEADER - rect.top) / Math.max(1, travel)));
        progressRef.current.style.transform = `scaleX(${progress})`;
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [progressRef]);

  return active;
}

function ContentsList({ active, className = '' }) {
  return (
    <ol className={`wp-contents ${className}`.trim()}>
      {CONTENTS.map(({ n, title, id }) => (
        <li key={id}>
          <a href={`#${id}`} aria-current={active === id ? 'location' : undefined}>
            <span className="pp-sec">§ {n}</span>
            <span className="wp-contents__title">{title}</span>
          </a>
        </li>
      ))}
    </ol>
  );
}

export default function WhitepaperPage() {
  const progressRef = useRef(null);
  const active = useReading(progressRef);

  useDocumentMeta({
    title: `White paper | ${PAPER.fullTitle}`,
    description: plain(PAPER.lead),
    path: '/whitepaper/',
  });

  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = id && document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, []);

  return (
    <>
      <a className="skip" href="#contents">Skip to the paper</a>
      <Nav variant="paper" />
      <div className="wp-progress" aria-hidden="true">
        <span ref={progressRef} />
      </div>
      <main id="main">
        <Cover
          runningHead={['QSI Labs', `White paper · ${PAPER.edition} · ${PAPER.date}`]}
          eyebrow={PAPER.edition}
          title={PAPER.title}
          subtitle={PAPER.subtitle}
          lead={<Rich text={PAPER.lead} />}
          actions={
            <>
              <a className="btn btn--download" href={PAPER.pdf} download>
                Download PDF
                <ArrowDown />
              </a>
              <a className="btn btn--ghost btn--arrow" href="#contents">
                Read online
                <ArrowRight />
              </a>
            </>
          }
          meta={[
            ['Website', PAPER.website],
            ['Date', PAPER.date],
          ]}
        />

        <div className="wp" data-nav-tone="light">
          <div className="container wp__layout">
            <aside className="wp__rail">
              <nav className="wp__toc" aria-label="Contents">
                <p className="pp-label">Contents</p>
                <ContentsList active={active} />
                <a className="wp__toc-pdf" href={PAPER.pdf} download>
                  <ArrowDown />
                  PDF · {PAPER.pages} pages
                </a>
              </nav>
            </aside>

            <article className="wp__paper" id="contents" aria-label={PAPER.fullTitle}>
              <p className="wp__notice">
                <span className="pp-label">Important notice.</span> {PAPER.notice}
              </p>

              <nav className="wp__inline-toc" aria-label="Contents">
                <p className="pp-label">Contents</p>
                <ContentsList active={active} className="wp-contents--inline" />
              </nav>

              {SECTIONS.map((section) => (
                <section
                  key={section.n}
                  className="pp-section"
                  id={sectionId(section.n)}
                  aria-labelledby={`${sectionId(section.n)}-title`}
                >
                  <p className="pp-section__num">
                    <span className="pp-sec">§ {section.n}</span>
                  </p>
                  <h2 className="pp-section__title" id={`${sectionId(section.n)}-title`}>
                    <Rich text={section.title} />
                  </h2>
                  {section.deck ? (
                    <p className="pp-deck">
                      <Rich text={section.deck} />
                    </p>
                  ) : null}
                  <Blocks blocks={section.blocks} />
                </section>
              ))}

              <section className="pp-section wp-refs" id={sectionId(15)} aria-labelledby={`${sectionId(15)}-title`}>
                <p className="pp-section__num">
                  <span className="pp-sec">§ 15</span>
                </p>
                <h2 className="pp-section__title" id={`${sectionId(15)}-title`}>
                  References
                </h2>
                <p className="pp-label wp-refs__note">
                  <Rich text={REFERENCES_NOTE} />
                </p>
                <ol className="wp-refs__list pp-wide">
                  {REFERENCES.map((reference, i) => (
                    <li key={reference} id={refId(i + 1)}>
                      <span className="wp-refs__n">[{i + 1}]</span>
                      <span>
                        <Rich text={reference} />
                      </span>
                    </li>
                  ))}
                </ol>
              </section>

              <footer className="wp__end">
                <p className="pp-label">
                  QSI Labs · White paper · {PAPER.edition} · {PAPER.date}
                </p>
                <div className="wp__end-actions">
                  <a className="btn btn--download" href={PAPER.pdf} download>
                    Download PDF
                    <ArrowDown />
                  </a>
                  <a className="btn btn--ghost" href="#top">
                    Back to the cover
                  </a>
                </div>
              </footer>
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
